import { setApplicationDocument } from '@/app-storage/applications/familyApplicationSlice'
import { AppRoutePaths } from '@/routing/route_paths'
import { AppDispatch, RootState } from '@/storage/store'
import FormIEP from '@assets/form_IEP.png'
import SampleBirthCertificate from '@assets/utah-birth-certificate.jpg'
import { useDispatch, useSelector } from 'react-redux'
import useUploadParams from '../../income_verification/hooks/useUploadParams'
import { RelationshipFormsSchema, SpecialEducationFormsSchema } from '@/apis/family_application/requests'
import { z } from 'zod'

export type SpecialEducationForms = z.infer<typeof SpecialEducationFormsSchema>
export type RelationshipForms = z.infer<typeof RelationshipFormsSchema>

interface IStudentDetailsForms {
  id: number
  name: string
  longName: string
  code: SpecialEducationForms | RelationshipForms
  description: string
  image: string
  additional_info?: string
}

export const specialEducationForms: IStudentDetailsForms[] = [
  {
    id: 2,
    name: 'form_iep',
    longName: 'special_education',
    code: 'form_IEP',
    description: 'form_iep_text',
    image: FormIEP,
  },
  {
    id: 3,
    name: 'form_sp',
    longName: 'special_education',
    code: 'form_service_plan',
    description: 'form_sp_text',
    image: FormIEP,
  },
  {
    id: 4,
    name: 'form_504',
    longName: 'special_education',
    code: 'form_504',
    description: 'form_504_text',
    image: FormIEP,
  },
  {
    id: 5,
    name: 'other_form',
    longName: 'special_education',
    code: 'form_other',
    description: 'other_form_text',
    image: FormIEP,
  },
] as const

const relationshipForms: IStudentDetailsForms[] = [
  {
    id: 6,
    name: 'relation_to_student',
    longName: 'documents',
    code: 'relation_to_student',
    description: 'relation_to_student_text',
    image: SampleBirthCertificate,
    additional_info: "relation_to_student_additional",
  }
] as const

const { student_details } = AppRoutePaths.family_application

function useStudenDetailsDocumentParams() {
  const { selectedMember, selectedOption, updateParams } = useUploadParams()

  const dispatch = useDispatch<AppDispatch>()

  const { data: application } = useSelector(
    (state: RootState) => state.FamilyApplication
  )

  function onFormSubmitted() {
    updateParams(student_details.success_verification)
    return
  }

  function onEnterForm(route: string, type: SpecialEducationForms) {
    updateParams(route, { selectedOption: type })
  }

  function saveImage(image: string) {
    dispatch(
      setApplicationDocument({
        // temporarily use fallbacks here. They should never be undefined.
        form: selectedOption as SpecialEducationForms,
        src: image,
        member: selectedMember ?? 'user',
      })
    )
  }

  function onStudentDetailFormSelect(value: SpecialEducationForms | RelationshipForms) {
    const path = value !== 'relation_to_student'
      ? student_details.special_education.capture_instructions
      : student_details.relationship.index

    updateParams(
      path,
      {selectedOption: value}
    )
  }

  function getSavedImage() {
    const document = application?.documents.find((document) => {
      if (
        document.documentMember === selectedMember &&
        document.document_type === selectedOption
      ) {
        return document
      }
      if (!selectedMember && document.document_type === selectedOption) {
        return document
      }
    })

    return { ...document, id: 2 }
  }

  function onRouteChange(route: string) {
    updateParams(route, {
      selectedOption: selectedOption,
      selectedMember: selectedMember,
    })
  }
  const info = () => {
    let forms = []
    
    forms.push(...specialEducationForms, ...relationshipForms)
    
    return forms.find((form) => form.code == selectedOption) ??
    specialEducationForms[0]
  }

  return {
    selectedOption,
    onRouteChange,
    info,
    onFormSubmitted,
    onEnterForm,
    document,
    getSavedImage,
    saveImage,
    onStudentDetailFormSelect,
  }
}

export default useStudenDetailsDocumentParams
